import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ZoomModal, ZoomNav, useDragPan, useZoomKeys } from './ZoomModal.tsx';
import { Plus, Minus, Maximize } from '../lib/icons.tsx';

/**
 * Read a slide properly: one slide, big, and zoomable.
 *
 * The grids everywhere else (deck overview, library, the add-slide gallery)
 * are cached WebP thumbnails — 960x540, half the 1920x1080 reference — because
 * a grid of live iframes evicts the Vite pool and re-runs a whole deck per
 * tile. That resolution is fine at tile size and useless zoomed. So the zoom
 * target is a LIVE frame instead, one at a time, opened deliberately.
 *
 * Why that's crisp: a slide is laid out entirely in `cqw` units off a
 * `container-type: inline-size` root (template/index.html), and nothing in the
 * shell applies `transform: scale`. So we zoom by RESIZING THE FRAME — every
 * `cqw` re-resolves and the slide re-lays-out at native device resolution.
 * Scaling with a transform would just magnify a bitmap; don't swap it in.
 *
 * `?only=<n>` renders that one slide rather than the whole deck (Shell.jsx).
 * Without it, panning a zoomed slide would scroll-snap to its neighbour, and
 * every other slide's video would be running behind this one.
 *
 * Give it `slideIdxs` and it becomes a reader rather than a single view: ‹ ›
 * and the arrow keys walk that list of slides. Each step swaps `?only=`, so the frame REMOUNTS
 * (`key={idx}`) rather than navigating in place — a navigated iframe pushes
 * onto the joint session history, which would turn the browser's Back button
 * into "previous slide". Zoom is deliberately kept across a step: the reason to
 * walk a deck zoomed in is to compare the same detail slide to slide.
 */

// Fit is 1. Below ~0.5 the frame is smaller than a thumbnail; above 4 the
// slide is 4x the window and panning stops being navigable.
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const STEP = 0.25;

const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(z * 100) / 100));

type SlidePreviewModalProps = {
  slug: string;
  slideIdx?: number;
  title?: string | null;
  /** The slides ‹ › may walk, as absolute deck indices — a RANGE, not a count,
   *  because a library item is a run of slides inside the middle of the bank
   *  deck and must not wander out of itself. Two or more turns nav on. */
  slideIdxs?: number[] | null;
  /** Names the slide you land on; without it the header keeps the title it
   *  opened with (the Dashboard passes the DECK's name, which is true of every
   *  slide in it). */
  titleFor?: (idx: number) => string | null | undefined;
  onClose: () => void;
};

export default function SlidePreviewModal({
  slug,
  slideIdx = 0,
  title,
  slideIdxs = null,
  titleFor,
  onClose,
}: SlidePreviewModalProps) {
  const [zoom, setZoom] = useState(1);
  const [idx, setIdx] = useState(slideIdx);
  // Which slide the frame has actually rendered — comparing against idx (rather
  // than a boolean reset on every step) is what keeps the skeleton up between
  // slides without a stale `loaded` letting the next frame show through black.
  const [loadedIdx, setLoadedIdx] = useState(-1);
  // The 16:9 box that exactly fits the scroller — zoom multiplies it.
  const [fit, setFit] = useState({ w: 0, h: 0 });
  const scrollerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Follow the host if it re-points an already-open modal at another slide.
  useEffect(() => setIdx(slideIdx), [slideIdx]);

  const loaded = loadedIdx === idx;
  const walk = slideIdxs && slideIdxs.length > 1 ? slideIdxs : null;
  const canNav = !!walk;
  // Position within the walk. A slide the host opened that isn't in the list
  // (shouldn't happen, but a stale list is one refresh away) reads as the
  // first, so the arrows still lead somewhere sane rather than dead-ending.
  const pos = walk ? Math.max(0, walk.indexOf(idx)) : 0;
  const step = useCallback(
    (delta: number) => {
      if (!walk) return;
      const at = Math.max(0, walk.indexOf(idx));
      const next = walk[Math.min(walk.length - 1, Math.max(0, at + delta))];
      if (next != null) setIdx(next);
    },
    [walk, idx],
  );
  const heading = (titleFor && titleFor(idx)) || title || 'Preview';
  const src = `/preview/${slug}/?only=${idx + 1}&bare=1&chrome=0&__studio=1`;

  // Measure the largest 16:9 box the scroller can show. Re-measured on resize
  // so the modal stays fitted when the window changes.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const measure = () => {
      const { clientWidth: cw, clientHeight: ch } = el;
      if (!cw || !ch) return;
      const w = Math.min(cw, (ch * 16) / 9);
      setFit({ w: Math.round(w), h: Math.round((w * 9) / 16) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Zooming should magnify what you were looking at, not jump to a corner. Hold
  // the viewport centre as a fraction of the content and restore it after the
  // frame resizes. It has to be a layout effect keyed on the new size: inside
  // the setState (or a rAF fired from it) the stage still has its OLD width, so
  // the assignment gets clamped to the old scroll range and the browser's
  // scroll anchoring then drags it to the end.
  const anchorRef = useRef({ x: 0.5, y: 0.5 });
  const zoomAbout = useCallback((next: (prev: number) => number) => setZoom((prev) => clampZoom(next(prev))), []);

  const zoomed = zoom > 1;
  const frameW = fit.w ? Math.round(fit.w * zoom) : 0;
  const frameH = fit.h ? Math.round(fit.h * zoom) : 0;

  function rememberAnchor(e: { currentTarget: HTMLDivElement }) {
    const el = e.currentTarget;
    if (el.scrollWidth > el.clientWidth) {
      anchorRef.current.x = (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth;
    }
    if (el.scrollHeight > el.clientHeight) {
      anchorRef.current.y = (el.scrollTop + el.clientHeight / 2) / el.scrollHeight;
    }
  }

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el || !frameW) return;
    const { x, y } = anchorRef.current;
    el.scrollLeft = x * el.scrollWidth - el.clientWidth / 2;
    el.scrollTop = y * el.scrollHeight - el.clientHeight / 2;
    // frameW/frameH are the post-zoom size, so this runs once the stage is big.
  }, [frameW, frameH]);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // The zoom keys are this viewer's own; the arrows and Escape are the shared
  // ones. Returning true says the key was consumed.
  useZoomKeys(canNav, step, (e) => {
    if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      zoomAbout((z) => z + STEP);
      return true;
    }
    if (e.key === '-' || e.key === '_') {
      e.preventDefault();
      zoomAbout((z) => z - STEP);
      return true;
    }
    if (e.key === '0') {
      e.preventDefault();
      zoomAbout(() => 1);
      return true;
    }
    return false;
  });

  // Cmd/Ctrl + wheel zooms (ctrl is also what a trackpad pinch sends).
  // Non-passive, because we preventDefault to stop the browser page-zooming.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.metaKey && !e.ctrlKey) return;
      e.preventDefault();
      zoomAbout((z) => z - Math.sign(e.deltaY) * STEP);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAbout]);

  // Drag to pan when zoomed in. The iframe swallows pointer events, so the
  // grab surface is an overlay that only exists while zoomed (below).
  const { handlers: pan } = useDragPan(scrollerRef);

  return (
    <ZoomModal
      ariaLabel={title ? `Preview: ${title}` : 'Slide preview'}
      onClose={onClose}
      closeRef={closeRef}
      heading={heading}
      nav={canNav ? <ZoomNav noun="slide" plural="Slides" pos={pos} total={walk.length} onStep={step} /> : null}
      controls={
        <div className="zoom__controls" role="group" aria-label="Zoom">
          <button
            type="button"
            className="iconbtn"
            onClick={() => zoomAbout((z) => z - STEP)}
            disabled={zoom <= MIN_ZOOM}
            title="Zoom out"
            aria-label="Zoom out"
          >
            <Minus />
          </button>
          <button
            type="button"
            className="zoom__level"
            onClick={() => zoomAbout(() => 1)}
            title="Fit to window (0)"
            aria-label={`Zoom ${Math.round(zoom * 100)} percent (click to fit)`}
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            className="iconbtn"
            onClick={() => zoomAbout((z) => z + STEP)}
            disabled={zoom >= MAX_ZOOM}
            title="Zoom in"
            aria-label="Zoom in"
          >
            <Plus />
          </button>
          <button
            type="button"
            className="iconbtn"
            onClick={() => zoomAbout(() => 2)}
            title="Zoom to 200%"
            aria-label="Zoom to 200 percent"
          >
            <Maximize />
          </button>
        </div>
      }
    >
      <div
        className={'zoom__scroller' + (zoomed ? ' zoom__scroller--pannable' : '')}
        ref={scrollerRef}
        onScroll={rememberAnchor}
      >
        <div className="zoom__stage" style={frameW ? { width: frameW, height: frameH } : undefined}>
          {!loaded && <div className="zoom__skeleton" aria-hidden="true" />}
          <iframe
            key={idx}
            className={'zoom__frame' + (loaded ? ' loaded' : '')}
            src={src}
            title={`Slide ${idx + 1}${heading ? `: ${heading}` : ''}`}
            onLoad={() => setLoadedIdx(idx)}
            allow="clipboard-write; fullscreen"
          />
          {/* Pan surface: an iframe eats pointer events, so dragging needs a
                sibling on top. Only mounted while zoomed, so at fit the frame
                stays interactive (links, video controls). */}
          {zoomed && <div className="zoom__pan" {...pan} onPointerCancel={pan.onPointerUp} />}
        </div>
      </div>

      <footer className="zoom__hint muted small">
        {canNav ? '← → for slides · ' : ''}
        {zoomed ? 'Drag to pan · ' : ''}⌘-scroll or +/− to zoom · 0 to fit · Esc to close
      </footer>
    </ZoomModal>
  );
}
